import { useContext, useEffect, useState } from "react"
import { AccountContext } from "../../../contexts/AccountContexts"
import { ProfilePicture } from "../../../components/ProfileComponents"
import { capitalizeFirstLetter, validatePassword } from "../../../utils"
import { ExpandableBubbleSidebar, ticketCategoryConfigs } from "../../../components/TicketComponents"
import { CiEdit } from "react-icons/ci"
import { MdDone } from "react-icons/md"
import { IoCloseSharp } from "react-icons/io5"
import { requestChangeProfileEmail, requestChangeProfileName, requestChangeProfilePassword } from "../../../requests/profile_requests"
import { passwordPattern } from "../../../entries"
import { ContentContext } from "../../../contexts/ContentContext"
import { PasswordRulesModule, UsernameRulesModule } from "../../../components/AuthComponents"
import { PageHeader } from "../../../components/HeaderComponents"
import { useTranslation } from "react-i18next"
import { BiCategory } from "react-icons/bi"



export const ProfileSettings = ({ }) => {
    const { t } = useTranslation('settings')
    const { user, language } = useContext(AccountContext)
    const { categories } = useContext(ContentContext)

    const [editInProgress, setEditInProgress] = useState(false)


    const changeName = (name) => {
        return requestChangeProfileName({ userId: user.id, name: name.replaceAll(" ", "").replaceAll("@", "") })
    }

    const changeEmail = (email) => {
        return requestChangeProfileEmail({ userId: user.id, email: email })
    }

    const getCategoryConfig = (category) => {
        return {
            status: categories[category] ? (categories[category]?.locales?.[language] ? categories[category]?.locales[language] : categories[category].title) : "UNK",
            style: categories[category] ? categories[category].style : "bg-primary text-white",
            icon: ticketCategoryConfigs[category] ? ticketCategoryConfigs[category].icon : <BiCategory />
        }
    }

    return (
        <div className="flex flex-col w-full bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 border-gray-300 dark:border-gray-600 rounded-md px-4 py-3 pb-5 gap-1">
            <PageHeader pageTitle={t("Profile Settings")} />

            <span>{t('Set up your profile.')}</span>
            <span className="text-red-600">{t('ATTENTION! Users will be able to see your username.')}</span>


            <div className="flex flex-col items-center gap-5">

                <ProfilePicture />
                <div className="flex flex-col items-center gap-1 relative w-full">

                    <ProfileRow title="Username" value={user.name} editInProgress={editInProgress} setEditInProgress={setEditInProgress} callback={changeName} />
                    <ProfileRow title="E-mail" value={user.email} editInProgress={editInProgress} setEditInProgress={setEditInProgress} callback={changeEmail} />
                    <PasswordRow title="Password" value="*********" editable={true} editInProgress={editInProgress} setEditInProgress={setEditInProgress} />
                    <ProfileRow title="Rank" value={capitalizeFirstLetter(user.rank)} editable={false} editInProgress={editInProgress} setEditInProgress={setEditInProgress} />

                </div>

                <div className="grid grid-cols-2 gap-2 items-center">
                    <span className="justify-self-end font-semibold">{t('My categories')}</span>
                    <div className="flex">
                        {user.categories &&
                            user.categories.map((category) =>
                                categories[category] &&
                                <ExpandableBubbleSidebar key={category} config={getCategoryConfig(category)} />
                            )
                        }
                        {
                            user.categories && user.categories.length === 0 &&
                            <span className="ltr:italic text-sm text-gray-800 dark:text-gray-300">{t('No assigned categories.')}</span>
                        }
                    </div>
                </div>
            </div>
        </div >
    )
}


const ProfileRow = ({ title, value, editable = true, editInProgress, setEditInProgress, callback }) => {
    const { t } = useTranslation('settings')
    const { user, setUser } = useContext(AccountContext)
    const { openErrorModal } = useContext(ContentContext)
    const [showEdit, setShowEdit] = useState(false)
    const [content, setContent] = useState(value)

    const handleClickEdit = () => {
        setShowEdit(true)
        setEditInProgress(true)
    }


    useEffect(() => {
        setContent(value)
    }, [value])

    const onConfirm = () => {
        callback(content).then((data) => {
            if (data) {
                setUser(data.user)
            }
            setShowEdit(false)
            setEditInProgress(false)
        }).catch((error) => {
            console.error(error.status, error.message)
            openErrorModal(error)
        })
    }

    const onClose = () => {
        setShowEdit(false)
        setContent(value)
        setEditInProgress(false)
    }

    return (
        <div className="relative w-full">
            {
                title === "Username" && showEdit &&
                <div className="absolute ltr:right-24 rtl:left-24">

                    <UsernameRulesModule username={content} />
                </div>
            }

            {
                editable && !showEdit && !editInProgress &&
                <div className="absolute ltr:right-3 rtl:left-3 peer">
                    <button className="btn btn-sm w-8 h-8 p-0 bg-inherit text-gray-800 dark:text-gray-300 hover:bg-primary dark:hover:bg-blue-600 hover:text-white group/test border-none shadow-none" onClick={handleClickEdit}><CiEdit size={20} /></button>
                </div>
            }
            {
                editable && showEdit &&
                <div className="absolute ltr:right-3 rtl:left-3 peer flex gap-2">
                    <button className="btn btn-sm w-8 h-8 p-0 bg-inherit text-gray-800 dark:text-gray-300 hover:bg-primary dark:hover:bg-blue-600 hover:text-white group/test border-none shadow-none" disabled={content === value} onClick={onConfirm}><MdDone size={20} /></button>
                    <button className="btn btn-sm w-8 h-8 p-0 bg-inherit text-gray-800 dark:text-gray-300 hover:bg-primary dark:hover:bg-blue-600 hover:text-white group/test border-none shadow-none" onClick={onClose}><IoCloseSharp size={20} /></button>
                </div>
            }
            {
                !showEdit &&
                <div className="grid grid-cols-2 gap-2 peer-hover:text-primary dark:peer-hover:text-blue-500 peer-hover:font-bold items-center">
                    <span className="justify-self-end font-semibold text-black dark:text-gray-400">{t(title)}</span>
                    <span className="justify-self-start">{t(value)}</span>
                </div>
            }
            {
                showEdit &&
                <div className="grid grid-cols-2 gap-2 items-center">
                    <span className="justify-self-end font-semibold">{t(title)}</span>
                    <input value={content}
                        onChange={(e) => {
                            if (e.target.value.length > 80) {
                                return
                            }
                            setContent(e.target.value)
                        }}
                        className="justify-self-start input input-bordered input-sm h-6 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300" />
                </div>
            }
        </div>
    )
}


const PasswordRow = ({ title, value, editable = true, editInProgress, setEditInProgress }) => {
    const { t } = useTranslation('settings')
    const { user, setUser } = useContext(AccountContext)
    const { openErrorModal } = useContext(ContentContext)

    const [showEdit, setShowEdit] = useState(false)

    const [newPassword, setNewPassword] = useState("")
    const [newRePassword, setNewRePassword] = useState("")

    const handleClickEdit = () => {
        setShowEdit(true)
        setEditInProgress(true)
    }



    const onConfirm = () => {
        requestChangeProfilePassword({ userId: user.id, password: newPassword })
            .then((data) => {
                setUser(data.user)
                setShowEdit(false)
                setEditInProgress(false)
                setNewPassword("")
                setNewRePassword("")
            })
            .catch((error) => {
                console.error(error.status, error.message)
                openErrorModal(error)
            })
    }

    const onClose = () => {
        setShowEdit(false)
        setEditInProgress(false)
        setNewPassword("")
        setNewRePassword("")
    }

    const submitCondition = validatePassword(newPassword) && validatePassword(newRePassword) && (newRePassword === newPassword)

    return (
        <div className="relative w-full">
            {
                showEdit &&

                <div className="absolute ltr:right-24 rtl:left-24">
                    <PasswordRulesModule password={newPassword} />
                </div>
            }
            {
                editable && !showEdit && !editInProgress &&
                <div className="absolute ltr:right-3 rtl:left-3 peer">
                    <button className="btn btn-sm w-8 h-8 p-0 bg-inherit text-gray-800 dark:text-gray-300 hover:bg-primary dark:hover:bg-blue-600 hover:text-white group/test border-none shadow-none" onClick={handleClickEdit}><CiEdit size={20} /></button>
                </div>
            }
            {
                editable && showEdit &&
                <div className="absolute ltr:right-3 rtl:left-3 peer flex gap-2">
                    <button className="btn btn-sm w-8 h-8 p-0 bg-inherit text-gray-800 dark:text-gray-300 hover:bg-primary dark:hover:bg-blue-600 hover:text-white group/test border-none shadow-none" disabled={!submitCondition} onClick={onConfirm}><MdDone size={20} /></button>
                    <button className="btn btn-sm w-8 h-8 p-0 bg-inherit text-gray-800 dark:text-gray-300 hover:bg-primary dark:hover:bg-blue-600 hover:text-white group/test border-none shadow-none" onClick={onClose}><IoCloseSharp size={20} /></button>
                </div>
            }
            {
                !showEdit &&
                <div className="grid grid-cols-2 gap-2 peer-hover:text-primary dark:peer-hover:text-blue-500 peer-hover:font-bold items-center">
                    <span className="justify-self-end font-semibold text-black dark:text-gray-400">{t(title)}</span>
                    <span className="justify-self-start">{t(value)}</span>
                </div>
            }
            {
                showEdit &&
                <div className="flex flex-col gap-2">
                    <div className="grid grid-cols-2 gap-2 items-center">
                        <span className="justify-self-end font-semibold">{t('New Password')}</span>
                        <input value={newPassword} type="password" pattern={passwordPattern} onChange={(e) => { setNewPassword(e.target.value) }} className="justify-self-start input input-bordered invalid:border-red-600 invalid:outline-red-600 input-sm h-6 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300" />
                    </div>
                    <div className="grid grid-cols-2 gap-2 items-center">
                        <span className="justify-self-end font-semibold">{t('Confirm New Password')}</span>
                        <input value={newRePassword} type="password" pattern={passwordPattern} onChange={(e) => { setNewRePassword(e.target.value) }} className="justify-self-start input input-bordered invalid:border-red-600 invalid:outline-red-600 input-sm h-6 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300" />
                    </div>
                </div>
            }
        </div>
    )
}